import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useMyMenuList } from "../menu/useMenu";
import { useUserInfo } from "../member/useMember";
import { useMenuChildrenList } from "../menu/useMenu";

/**
 * 페이지별 권한을 관리하는 커스텀 훅
 * 현재 페이지의 URL을 기반으로 해당 페이지의 권한을 반환합니다.
 *
 * @returns {Object} 권한 객체
 * @property {boolean} canRead - 조회 권한
 * @property {boolean} canCreate - 추가 권한
 * @property {boolean} canUpdate - 수정 권한
 * @property {boolean} canDelete - 삭제 권한
 * @property {boolean} canSave - 저장 권한
 * @property {boolean} canPrint - 출력 권한
 * @property {boolean} canExport - 엑셀 권한
 * @property {boolean} isLoading - 권한 로딩 중 여부
 * @property {boolean} allAllowed - 모든 권한 허용 여부
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   const permission = usePagePermission();
 *
 *   return (
 *     <div>
 *       <Button disabled={!permission.canCreate}>추가</Button>
 *       <Button disabled={!permission.canUpdate}>수정</Button>
 *       <Button disabled={!permission.canDelete}>삭제</Button>
 *       <Button disabled={!permission.canSave}>저장</Button>
 *       <Button disabled={!permission.canPrint}>출력</Button>
 *       <Button disabled={!permission.canExport}>엑셀</Button>
 *     </div>
 *   );
 * }
 * ```
 */
export function usePagePermission() {
  const location = useLocation();
  const currentPath = location.pathname;

  // 현재 로그인한 사용자 정보 가져오기
  const { data: userInfo, isLoading: isUserLoading } = useUserInfo();

  // 전체 메뉴 리스트 가져오기 (path와 menuId 매핑용)
  const { data: menuList, isLoading: isMenuLoading } = useMenuChildrenList();

  // 사용자의 메뉴별 권한 리스트 가져오기
  const { data: myMenuResponse, isLoading: isPermissionLoading } = useMyMenuList(userInfo?.name || "");

  // MyMenuResponse에서 all 배열 추출
  const userMenuPermissions = myMenuResponse?.all || [];

  // 현재 페이지의 권한 계산
  const permission = useMemo(() => {
    // 로딩 중이거나 데이터가 없으면 기본값 (모든 권한 비활성화)
    if (isUserLoading || isMenuLoading || isPermissionLoading || !userInfo || !menuList) {
      return {
        canRead: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        canSave: false,
        canPrint: false,
        canExport: false,
        allAllowed: false,
        isLoading: true,
      };
    }

    // userMenuPermissions가 배열인지 확인 (배열이 비어있는 경우 허용)
    if (!Array.isArray(userMenuPermissions)) {
      return {
        canRead: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        canSave: false,
        canPrint: false,
        canExport: false,
        allAllowed: false,
        isLoading: false,
      };
    }

    // 현재 경로에 해당하는 메뉴 찾기
    const currentMenu = menuList.find(menu => menu.path === currentPath);

    if (!currentMenu) {
      // 메뉴에 없는 페이지는 기본적으로 모든 권한 허용 (예: 로그인 페이지 등)
      return {
        canRead: true,
        canCreate: true,
        canUpdate: true,
        canDelete: true,
        canSave: true,
        canPrint: true,
        canExport: true,
        allAllowed: true,
        isLoading: false,
      };
    }

    // 현재 메뉴의 권한 찾기 (path로 매칭)
    const menuPermission = userMenuPermissions.find(perm => perm.path === currentMenu.path);

    if (!menuPermission) {
      // 권한 설정이 없으면 모든 권한 비활성화
      return {
        canRead: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        canSave: false,
        canPrint: false,
        canExport: false,
        allAllowed: false,
        isLoading: false,
      };
    }

    // 권한 반환
    return {
      canRead: menuPermission.canRead ?? false,
      canCreate: menuPermission.canCreate ?? false,
      canUpdate: menuPermission.canUpdate ?? false,
      canDelete: menuPermission.canDelete ?? false,
      canSave: menuPermission.canSave ?? false,
      canPrint: menuPermission.canPrint ?? false,
      canExport: menuPermission.canExport ?? false,
      allAllowed: menuPermission.allAllowed ?? false,
      isLoading: false,
    };
  }, [currentPath, userInfo, menuList, userMenuPermissions, isUserLoading, isMenuLoading, isPermissionLoading]);

  return permission;
}
